import Builder from 'react-native-jala-orm/src/model/Builder';
import { QueryBuilder } from 'react-native-jala-orm/src/query/Builder';
import _ from 'lodash';
import Expression from 'react-native-jala-orm/src/query/Expression';
import SyncableModel from './SyncableModel';
import { getRootStore } from "../utils/root-store"
import { Platform } from "react-native"

export default class Farm extends SyncableModel {
    static name = 'Farm'

    _fillable = [
      'id', 'name', 'established_since', 'timezone',
      'latitude', 'longitude',
      'region_id', 'address',
      'created_at', 'updated_at',
      'area', 'country_id',
      'deleted_at',
      'state', 'remark',
      'batch_margin',
      'synced_at', 'currency_id'
    ]

    _casts = {
      area: 'integer',
      farm_user_role_id: 'integer',
      state: 'object'
    }

    _appends = [
      'is_can_be_pushed',
      'is_modified',
      'is_not_synced',
      'province_id',
      'regency_id',
      'district_id',
      'village_id',
    ]

    static endpoint = '/farms'

    static searchables = ['name']

    // Handling cycle dependencies
    static FarmUser = SyncableModel

    static _boot() {
      super._boot();

      this.addGlobalScope('global', (query: Builder) => {
        if (_.isEmpty(query.getQuery().columns)) {
          query.select([query.qualifyColumn('*')]);
        }

        const { userStore: { user } } = getRootStore();
        if (user && user.id) {
          const { FarmUser } = this;
          const farmUser = (new FarmUser()).setTable(`${query.getModel().getTable()}_farm_user`);
          query.leftJoin(`${(new FarmUser()).getTable()} as ${farmUser.getTable()}`, (q1: QueryBuilder) => {
            q1.whereColumn(farmUser.qualifyColumn('farm_id'), query.getModel().getQualifiedKeyName())
              .where(farmUser.qualifyColumn('user_id'), user.id);
          });
        }
      });
    }

    qualifyColumn(column: string): string {
      const { userStore: { user } } = getRootStore();
      if (column === '*') {
        return new Expression([
          `${this.getTable()}.*`,
          ...((user && user.id) ? [
            `${this.qualifyColumn('farm_user_role_id')} as farm_user_role_id`,
          ] : []),
          `${this.qualifyColumn('province_id')} as province_id`,
          `${this.qualifyColumn('regency_id')} as regency_id`,
          `${this.qualifyColumn('district_id')} as district_id`,
          `${this.qualifyColumn('village_id')} as village_id`,
        ].join(', '));
      }
      if (column === 'farm_user_role_id') {
        const { FarmUser } = this.constructor;
        const farmUser = (new FarmUser()).setTable(`${this.getTable()}_farm_user`);
        return farmUser.qualifyColumn('role_id');
      }
      if (column === 'province_id') {
        return new Expression(`SUBSTR(${this.qualifyColumn('region_id')}, 1, 2)`);
      }
      if (column === 'regency_id') {
        return new Expression(`SUBSTR(${this.qualifyColumn('region_id')}, 1, 4)`);
      }
      if (column === 'district_id') {
        return new Expression(`SUBSTR(${this.qualifyColumn('region_id')}, 1, 7)`);
      }
      if (column === 'village_id') {
        return this.qualifyColumn('region_id');
      }
      return super.qualifyColumn(column);
    }

    getProvinceIdAttribute() {
      return this.getAttribute('region_id') && this.getAttribute('region_id').substring(0, 2);
    }

    setProvinceIdAttribute() {
      return this;
    }

    getRegencyIdAttribute() {
      return this.getAttribute('region_id') && this.getAttribute('region_id').substring(0, 4);
    }

    setRegencyIdAttribute() {
      return this;
    }

    getDistrictIdAttribute() {
      return this.getAttribute('region_id') && this.getAttribute('region_id').substring(0, 7);
    }

    setDistrictIdAttribute() {
      return this;
    }

    getVillageIdAttribute() {
      return this.getAttribute('region_id');
    }

    setVillageIdAttribute() {
      return this;
    }

    static async fetch(params = {}) {
      const { userStore: { user } } = getRootStore();
      const _params = { ...params };
      if (user && user.role_id === 1 && !_params.id__in && Platform.OS === 'web') {
        _params.has = 'farm_user';
      }
      return super.fetch(_params);
    }

    static async syncAll(
      params = {}, sqlParams = [], cleanAfterSync = true,
      onSyncProgressUpdate = undefined, onError = undefined
    ) {
      const { userStore: { user } } = getRootStore();
      const _params = { ...params };
      const _sqlParams = [...sqlParams];
      if (user && user.role_id === 1 && !_params.id__in) {
        _params.has = 'farm_user';
      }
      return super.syncAll(_params, _sqlParams, cleanAfterSync, onSyncProgressUpdate, onError);
    }
}
